import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Radio, Anchor, Flame, DollarSign, Award, AlertOctagon, ArrowRight, Instagram, Facebook } from "lucide-react";
import api from "../lib/api";

const TYPE_ICON = {
    boarding: Anchor,
    fuel: Flame,
    revenue: DollarSign,
    level_up: Award,
    strike: AlertOctagon,
    breaking: Radio,
};

const TYPE_COLOR = {
    boarding: "hsl(45 85% 68%)",
    fuel: "hsl(25 90% 60%)",
    revenue: "hsl(153 60% 60%)",
    level_up: "hsl(45 85% 75%)",
    strike: "hsl(350 75% 65%)",
    breaking: "hsl(0 85% 65%)",  // red — Director-mandated BREAKING urgency
};

/**
 * Fleet Signal HUD — a thin Sovereign Star ticker that mounts in the header
 * of every vessel (Trade, Sports, TechOps, TechOpsSA, Foundry, Solana, Intel).
 *
 * Pulls from the public /api/sovereign-star/feed (no auth required), surfaces
 * the latest 5 signals (BREAKING pinned first), and rotates through them on a
 * 5-second cadence with a horizontal-scroll fallback on hover.
 *
 * Render mode = "bar" (full-width ticker bar) | "compact" (single-line strip)
 */
export default function FleetSignalBar({ mode = "bar", className = "" }) {
    const [signals, setSignals] = useState([]);
    const [idx, setIdx] = useState(0);
    const [paused, setPaused] = useState(false);
    const intervalRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const { data } = await api.get("/sovereign-star/feed?limit=5");
                if (cancelled) return;
                const items = (data?.items || []).slice(0, 5);
                setSignals(items);
            } catch {
                /* silent — HUD just stays empty */
            }
        };
        load();
        const refresh = setInterval(load, 60000); // refresh every minute
        return () => {
            cancelled = true;
            clearInterval(refresh);
        };
    }, []);

    useEffect(() => {
        if (paused || signals.length <= 1) return;
        intervalRef.current = setInterval(() => {
            setIdx((i) => (i + 1) % signals.length);
        }, 5000);
        return () => clearInterval(intervalRef.current);
    }, [paused, signals.length]);

    if (!signals.length) {
        // Render an idle placeholder so layout doesn't shift on hydration.
        return (
            <div
                data-testid="fleet-signal-bar"
                data-state="idle"
                className={`relative w-full border-b border-[hsl(40_40%_25%/0.25)] bg-[hsl(151_55%_5%/0.7)] backdrop-blur-sm ${className}`}
            >
                <div className="max-w-[1400px] mx-auto px-4 py-2 flex items-center gap-3">
                    <FleetBadge />
                    <span className="text-[10px] uppercase tracking-[0.4em] text-muted-foreground/55 font-mono truncate">
                        Welding signal bridges · feed warming up
                    </span>
                </div>
            </div>
        );
    }

    const current = signals[idx];
    const Icon = TYPE_ICON[current.type] || Radio;
    const color = TYPE_COLOR[current.type] || "hsl(45 85% 68%)";
    const isBreaking = current.type === "breaking" || current.pinned;

    return (
        <div
            data-testid="fleet-signal-bar"
            data-state="active"
            data-breaking={isBreaking ? "1" : "0"}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            className={`relative w-full border-b backdrop-blur-sm transition-colors ${
                isBreaking
                    ? "border-[hsl(0_85%_55%/0.6)] bg-[linear-gradient(90deg,hsl(0_60%_8%/0.92)_0%,hsl(0_70%_12%/0.92)_50%,hsl(0_50%_8%/0.92)_100%)]"
                    : "border-[hsl(40_40%_25%/0.3)] bg-[hsl(151_55%_5%/0.7)]"
            } ${className}`}
        >
            <div className="max-w-[1400px] mx-auto px-4 py-2 flex items-center gap-3">
                <FleetBadge breaking={isBreaking} />

                <div className="flex-1 min-w-0 overflow-hidden">
                    <div className="flex items-center gap-2.5 animate-fade-in" key={current.id}>
                        <Icon
                            size={13}
                            style={{ color, flexShrink: 0 }}
                            strokeWidth={2}
                        />
                        {current.headline && (
                            <span
                                className="hidden md:inline text-[9px] uppercase tracking-[0.4em] font-mono shrink-0"
                                style={{ color }}
                            >
                                {current.headline}
                            </span>
                        )}
                        <span className="text-xs sm:text-sm text-foreground/90 truncate">
                            <span className="font-medium">{current.title}</span>
                            {current.body && (
                                <span className="text-foreground/55 hidden lg:inline">
                                    {" "}— {current.body}
                                </span>
                            )}
                        </span>
                        {current.vessel && (
                            <span className="hidden sm:inline text-[9px] uppercase tracking-[0.35em] text-muted-foreground/60 font-mono shrink-0">
                                · {current.vessel}
                                {current.state ? ` · ${current.state}` : ""}
                            </span>
                        )}
                    </div>
                </div>

                {/* Pagination dots */}
                <div className="hidden sm:flex items-center gap-1 shrink-0">
                    {signals.map((_, i) => (
                        <span
                            key={i}
                            className={`h-1 w-1 rounded-full transition-all ${
                                i === idx
                                    ? "bg-[hsl(45_85%_68%)] w-3"
                                    : "bg-[hsl(40_40%_25%/0.6)]"
                            }`}
                        />
                    ))}
                </div>

                <Link
                    to="/#sovereign-star"
                    data-testid="fleet-signal-link"
                    className="text-[9px] uppercase tracking-[0.35em] text-muted-foreground hover:text-[hsl(45_85%_68%)] transition-colors flex items-center gap-1 shrink-0 font-mono"
                >
                    Star <ArrowRight size={10} />
                </Link>

                {/* Sovereign Social Cloak · centralized /ig and /fb slugs */}
                <div className="hidden md:flex items-center gap-2 shrink-0 pl-3 ml-1 border-l border-[hsl(40_40%_25%/0.35)]">
                    <Link
                        to="/ig"
                        data-testid="signal-social-instagram"
                        aria-label="Instagram · empowerinvestgrow"
                        className="text-muted-foreground/70 hover:text-[#E8C56C] transition-colors"
                    >
                        <Instagram size={12} />
                    </Link>
                    <Link
                        to="/fb"
                        data-testid="signal-social-facebook"
                        aria-label="Facebook · empowerinvestgrow"
                        className="text-muted-foreground/70 hover:text-[#00E5FF] transition-colors"
                    >
                        <Facebook size={12} />
                    </Link>
                </div>
            </div>
        </div>
    );
}

function FleetBadge({ breaking = false }) {
    return (
        <div
            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full shrink-0"
            style={{
                background: "rgba(0,0,0,0.45)",
                border: `1px solid ${
                    breaking ? "hsl(0 85% 60% / 0.7)" : "hsl(45 85% 68% / 0.35)"
                }`,
            }}
        >
            <span className="relative flex h-1.5 w-1.5">
                <span
                    className="absolute inline-flex h-full w-full rounded-full opacity-90 animate-ping"
                    style={{ background: breaking ? "hsl(0 85% 60%)" : "hsl(45 85% 68%)" }}
                />
                <span
                    className="relative inline-flex rounded-full h-1.5 w-1.5"
                    style={{ background: breaking ? "hsl(0 90% 65%)" : "hsl(45 85% 68%)" }}
                />
            </span>
            <span
                className="text-[9px] uppercase tracking-[0.4em] font-bold font-mono"
                style={{ color: breaking ? "hsl(0 85% 75%)" : "hsl(45 85% 68%)" }}
            >
                {breaking ? "Breaking" : "Fleet Signal"}
            </span>
        </div>
    );
}
